/**
 * PATH: packages/ui/src/components/email/avatar.ts
 * PURPOSE:
 *   - Pure helpers for rendering an email sender's avatar bubble:
 *     pick a deterministic Tailwind background colour from the
 *     sender's name, and derive 2-letter initials.
 *
 * ROLE IN ARCHITECTURE:
 *   - Universal helpers consumed by the universal `<EmailRow>`
 *     primitive in this package, and re-exported from the
 *     `@quotech/ui` barrel so host apps stop duplicating the
 *     same 6-line functions in every email page.
 *   - Lifted from `apps/sam/src/features/email/lib/avatar.ts`
 *     (was: `frontend/src/features/email/lib/avatar.ts`) by
 *     Stack 6 PR 2b. Pre-import history:
 *     https://gitlab.com/vast-dreams/all-styled-steel-dashboard/-/blame/main/frontend/src/features/email/lib/avatar.ts
 *
 * NOTES FOR FUTURE AI:
 *   - Keep this file pure (no React, no Tailwind preset coupling
 *     beyond returning class strings). Any host app can override
 *     the palette by passing a different list to a future
 *     `pickAvatarColor` overload — for now the palette is shared.
 */

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-pink-500',
  'bg-indigo-500',
  'bg-teal-500',
  'bg-orange-500',
  'bg-fuchsia-500',
  'bg-lime-500',
] as const

export function getAvatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}
